import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Link, Plus, Edit, Trash2, Search, Hotel, Building, Target } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';

const RoomTypeMappings = () => {
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState('');
  const [hotelRoomTypes, setHotelRoomTypes] = useState([]);
  const [competitorRoomTypes, setCompetitorRoomTypes] = useState([]);
  const [competitorHotels, setCompetitorHotels] = useState([]);
  const [roomMappings, setRoomMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompetitorHotel, setSelectedCompetitorHotel] = useState('');
  const [newMapping, setNewMapping] = useState({
    hotel_room_type_id: '',
    competitor_hotel_name: '',
    competitor_room_type: '',
    mapping_confidence: 1.0,
    notes: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedHotel) {
      fetchHotelData(selectedHotel);
    }
  }, [selectedHotel]);

  useEffect(() => {
    if (selectedCompetitorHotel) {
      fetchCompetitorRoomTypes(selectedCompetitorHotel);
    }
  }, [selectedCompetitorHotel]);

  const fetchInitialData = async () => {
    try {
      // Fetch hotels
      const brandsResponse = await fetch('/api/tenants/brands', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` },
      });
      
      if (brandsResponse.ok) {
        const brandsData = await brandsResponse.json();
        const allHotels = [];
        
        for (const brand of brandsData.brands || []) {
          const hotelsResponse = await fetch(`/api/tenants/brands/${brand.id}/hotels`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` },
          });
          if (hotelsResponse.ok) {
            const hotelsData = await hotelsResponse.json();
            const hotelsWithBrand = (hotelsData.hotels || []).map(hotel => ({
              ...hotel,
              brand_name: brand.name
            }));
            allHotels.push(...hotelsWithBrand);
          }
        }
        setHotels(allHotels);
        
        if (allHotels.length > 0 && !selectedHotel) {
          setSelectedHotel(allHotels[0].id);
        }
      }

      // Fetch competitor hotels
      const competitorResponse = await fetch('/api/tenants/competitor-hotels-test?limit=20', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` },
      });
      if (competitorResponse.ok) {
        const competitorData = await competitorResponse.json();
        setCompetitorHotels(competitorData.hotels || []);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHotelData = async (hotelId) => {
    try {
      // Fetch hotel room types
      const roomTypesResponse = await fetch(`/api/tenants/hotels/${hotelId}/room-types`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` },
      });
      if (roomTypesResponse.ok) {
        const roomTypesData = await roomTypesResponse.json();
        setHotelRoomTypes(roomTypesData.room_types || []);
      }

      // Fetch existing mappings
      const mappingsResponse = await fetch(`/api/tenants/hotels/${hotelId}/room-mappings`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` },
      });
      if (mappingsResponse.ok) {
        const mappingsData = await mappingsResponse.json();
        setRoomMappings(mappingsData.room_mappings || []);
      }
    } catch (error) {
      console.error('Error fetching hotel data:', error);
    }
  };

  const fetchCompetitorRoomTypes = async (hotelIdentifier) => {
    try {
      const response = await fetch(`/api/tenants/competitor-hotels/${encodeURIComponent(hotelIdentifier)}/room-types`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCompetitorRoomTypes(data.room_types || []);
      }
    } catch (error) {
      console.error('Error fetching competitor room types:', error);
    }
  };

  const createMapping = async () => {
    if (!selectedHotel || !newMapping.hotel_room_type_id || !newMapping.competitor_hotel_name || !newMapping.competitor_room_type) {
      return;
    }
    
    setCreating(true);
    try {
      const response = await fetch(`/api/tenants/hotels/${selectedHotel}/room-mappings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwt')}`,
        },
        body: JSON.stringify(newMapping),
      });
      
      if (response.ok) {
        const data = await response.json();
        setRoomMappings([...roomMappings, data.room_mapping]);
        setNewMapping({
          hotel_room_type_id: '',
          competitor_hotel_name: '',
          competitor_room_type: '',
          mapping_confidence: 1.0,
          notes: ''
        });
        setSelectedCompetitorHotel('');
        setIsCreateModalOpen(false);
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to create mapping');
      }
    } catch (error) {
      console.error('Error creating mapping:', error);
    } finally {
      setCreating(false);
    }
  };

  const deleteMapping = async (mappingId) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) return;
    
    try {
      const response = await fetch(`/api/tenants/room-mappings/${mappingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` },
      });
      
      if (response.ok) {
        setRoomMappings(roomMappings.filter(m => m.id !== mappingId));
      }
    } catch (error) {
      console.error('Error deleting mapping:', error);
    }
  };

  const filteredCompetitorHotels = competitorHotels.filter(hotel =>
    hotel.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Helper function to get hotel name from ID or return the name if it's already a name
  const getCompetitorHotelName = (hotelIdentifier) => {
    // If it's a number (hotel ID), try to find the hotel name
    if (/^\d+$/.test(hotelIdentifier)) {
      const hotel = competitorHotels.find(h => h.id === hotelIdentifier);
      return hotel ? hotel.name : hotelIdentifier;
    }
    // If it's already a name, return as is
    return hotelIdentifier;
  };

  // Filter room mappings based on search term
  const filteredRoomMappings = roomMappings.filter(mapping => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const hotelName = getCompetitorHotelName(mapping.competitor_hotel_name);
    return (
      mapping.hotel_room_types?.name?.toLowerCase().includes(searchLower) ||
      hotelName?.toLowerCase().includes(searchLower) ||
      mapping.competitor_room_type?.toLowerCase().includes(searchLower) ||
      mapping.notes?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading mappings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Room Type Mappings</h2>
          <p className="text-muted-foreground">
            Map your room types to competitor room types for pricing analysis
          </p>
        </div>
        
        {selectedHotel && hotelRoomTypes.length > 0 && (
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search mappings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
          </div>
        )}
        
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogTrigger asChild>
            <Button className="flex items-center space-x-2" disabled={!selectedHotel || hotelRoomTypes.length === 0}>
              <Plus className="h-4 w-4" />
              <span>Create Mapping</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Room Type Mapping</DialogTitle>
              <DialogDescription>
                Map one of your room types to a competitor's room type
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="hotel-room-type">Your Room Type</Label>
                <Select 
                  value={newMapping.hotel_room_type_id} 
                  onValueChange={(value) => setNewMapping({...newMapping, hotel_room_type_id: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your room type" />
                  </SelectTrigger>
                  <SelectContent>
                    {hotelRoomTypes.map((roomType) => (
                      <SelectItem key={roomType.id} value={roomType.id}>
                        {roomType.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="competitor-hotel-search">Competitor Hotel</Label>
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search competitor hotels..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select 
                    value={selectedCompetitorHotel} 
                    onValueChange={(value) => {
                      setSelectedCompetitorHotel(value);
                      setNewMapping({...newMapping, competitor_hotel_name: value});
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select competitor hotel" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredCompetitorHotels.slice(0, 20).map((hotel) => (
                        <SelectItem key={hotel.id} value={hotel.name}>
                          {hotel.name} - {hotel.location}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {selectedCompetitorHotel && (
                <div className="space-y-2">
                  <Label htmlFor="competitor-room-type">Competitor Room Type</Label>
                  <Select 
                    value={newMapping.competitor_room_type} 
                    onValueChange={(value) => setNewMapping({...newMapping, competitor_room_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select competitor room type" />
                    </SelectTrigger>
                    <SelectContent>
                      {competitorRoomTypes.map((roomType, index) => (
                        <SelectItem key={index} value={roomType.name}>
                          {roomType.name} (Max: {roomType.max_occupancy} guests)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="confidence">Mapping Confidence</Label>
                <Select 
                  value={newMapping.mapping_confidence.toString()} 
                  onValueChange={(value) => setNewMapping({...newMapping, mapping_confidence: parseFloat(value)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1.0">High (1.0) - Exact match</SelectItem>
                    <SelectItem value="0.8">Good (0.8) - Very similar</SelectItem>
                    <SelectItem value="0.6">Medium (0.6) - Somewhat similar</SelectItem>
                    <SelectItem value="0.4">Low (0.4) - Different but comparable</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Add any notes about this mapping..."
                  value={newMapping.notes}
                  onChange={(e) => setNewMapping({...newMapping, notes: e.target.value})}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={createMapping} 
                disabled={creating || !newMapping.hotel_room_type_id || !newMapping.competitor_hotel_name || !newMapping.competitor_room_type}
              >
                {creating ? 'Creating...' : 'Create Mapping'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Hotel Selection */}
      {hotels.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Hotel className="h-5 w-5" />
              <span>Select Hotel</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedHotel} onValueChange={setSelectedHotel}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choose a hotel to manage mappings" />
              </SelectTrigger>
              <SelectContent>
                {hotels.map((hotel) => (
                  <SelectItem key={hotel.id} value={hotel.id}>
                    {hotel.name} ({hotel.brand_name})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Mappings Display */}
      {hotels.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Hotel className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No hotels available</h3>
            <p className="text-muted-foreground text-center">
              Create hotels and room types first before creating mappings
            </p>
          </CardContent>
        </Card>
      ) : !selectedHotel ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Link className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Select a hotel</h3>
            <p className="text-muted-foreground text-center">
              Choose a hotel above to view and manage its room type mappings
            </p>
          </CardContent>
        </Card>
      ) : hotelRoomTypes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No room types defined</h3>
            <p className="text-muted-foreground text-center">
              Define room types for this hotel first before creating mappings
            </p>
          </CardContent>
        </Card>
      ) : roomMappings.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Link className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No mappings yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Create your first room type mapping to start competitor analysis
            </p>
            <Button onClick={() => setIsCreateModalOpen(true)} className="flex items-center space-x-2">
              <Plus className="h-4 w-4" />
              <span>Create First Mapping</span>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Link className="h-5 w-5" />
                <span>Room Type Mappings</span>
                <Badge variant="secondary">
                  {searchTerm ? `${filteredRoomMappings.length} of ${roomMappings.length}` : roomMappings.length}
                </Badge>
              </div>
              <Button onClick={() => setIsCreateModalOpen(true)} size="sm" className="flex items-center space-x-2">
                <Plus className="h-4 w-4" />
                <span>Add Mapping</span>
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Your Room Type</TableHead>
                  <TableHead>Competitor Hotel</TableHead>
                  <TableHead>Competitor Room Type</TableHead>
                  <TableHead>Confidence</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRoomMappings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <div className="text-muted-foreground">
                        {searchTerm ? 'No mappings match your search' : 'No mappings found'}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRoomMappings.map((mapping) => (
                  <TableRow key={mapping.id}>
                    <TableCell>
                      <Badge variant="default">{mapping.hotel_room_types?.name}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{getCompetitorHotelName(mapping.competitor_hotel_name)}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{mapping.competitor_room_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1">
                        <Target className="h-4 w-4 text-muted-foreground" />
                        <span>{(mapping.mapping_confidence * 100).toFixed(0)}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs truncate text-sm text-muted-foreground">
                        {mapping.notes || '-'}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button variant="ghost" size="sm">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-destructive hover:text-destructive"
                          onClick={() => deleteMapping(mapping.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RoomTypeMappings;
